/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
import { observer } from 'mobx-react-lite'
import { WarningCircle } from 'iconoir-react'
import { withZod } from '@remix-validated-form/with-zod'
import { useField } from 'remix-validated-form'
import { z } from 'zod'
import { Button } from 'app/components/ui/button'
import { FormInput } from 'app/components/ui/form-input'
import { SmartForm, SubAction } from 'app/components/ui/smart-form'
import { FormPanelShadcnComponent } from 'app/components/form-panel/FormPanelShadcnComponent'
import { FloatingSaveShadcnComponent } from 'app/components/floating-save/FloatingSaveShadcnComponent'
import { useFloatingSave } from 'app/components/floating-save/useFloatingSave'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeaderComponent } from 'app/components/page-header/PageHeaderComponent'
import type { WorkspaceJson } from 'app/types/entities'

const workspaceSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, { message: `Workspace name is required` }),
  promptAddendum: z.string().optional(),
})

export type WorkspaceFormData = z.infer<typeof workspaceSchema>
export const workspaceValidator = withZod(workspaceSchema)

export type WorkspaceSettingsComponentProps = {
  workspace: WorkspaceJson | undefined
  canEditWorkspace: boolean
  isDirtyForm: boolean
  isLoading: boolean
  hasLocations: boolean
  isSuperUser: boolean
  formRef: React.RefObject<HTMLFormElement | null>
  subaction: SubAction
  deleteHandler: () => void
  submitHandler: (data?: WorkspaceFormData) => void | Promise<void>
  handleTouchedFields: (touched: boolean) => void
}

export const WorkspaceSettingsComponent = observer(function WorkspaceSettingsComponent({
  workspace,
  canEditWorkspace,
  isDirtyForm,
  isLoading,
  hasLocations,
  isSuperUser,
  formRef,
  subaction,
  deleteHandler,
  submitHandler,
  handleTouchedFields,
}: WorkspaceSettingsComponentProps) {
  const floatingSave = useFloatingSave({ isDirtyForm })

  if (!workspace) return null

  return (
    <PageScroll header={<PageHeaderComponent title="Workspace" />}>
      <SmartForm
        key={`${workspace.id}-${workspace.version ?? 0}`}
        id="workspace-form"
        className="-mt-0 gap-y-6"
        hideButtons
        preventSubmit
        validator={workspaceValidator as any}
        defaultValues={workspace}
        subaction={subaction}
        hiddenInputs={[{ name: `id`, value: workspace?.id }]}
        formRef={formRef}
        onSubmit={submitHandler}
        onTouchedFields={handleTouchedFields}
      >
        <FormPanelShadcnComponent title="Workspace Name" cols={6}>
          <FormInput
            name="name"
            label="Workspace Name"
            className="input-large"
            labelClassName="required lbl"
            disabled={!canEditWorkspace}
          />
        </FormPanelShadcnComponent>

        {isSuperUser && (
          <FormPanelShadcnComponent title="Prompt Addendum" cols={6}>
            <FormTextArea
              rows={3}
              name="promptAddendum"
              label="Prompt Addendum"
              className="min-h-[80px]"
              labelClassName="lbl"
            />
            <div className="mt-5 flex w-full items-center justify-between rounded bg-amber-100 p-3">
              <div className="flex items-center">
                <div className="mr-4 flex h-8 w-8 items-center justify-center rounded-full bg-amber-200">
                  <WarningCircle className="h-5 w-5 text-yellow-800" />
                </div>
                <div className="text-body-sm text-default">Don't forget to reprocess calls manually</div>
              </div>
            </div>
          </FormPanelShadcnComponent>
        )}

        <div className="col-span-6 flex gap-4">
          {isSuperUser && (
            <Button variant="danger" size="md" loading={isLoading} disabled={hasLocations} onClick={deleteHandler}>
              Delete
            </Button>
          )}
          <div className="grow" />
        </div>

        {canEditWorkspace && (
          <FloatingSaveShadcnComponent
            visible={floatingSave.visible}
            isSaving={floatingSave.isSaving}
            buttonType={floatingSave.buttonType}
            buttonLabel="Save"
            onSave={floatingSave.onSave}
          />
        )}
      </SmartForm>
    </PageScroll>
  )
})

function FormTextArea({
  name,
  label,
  rows = 6,
  className,
  labelClassName,
}: {
  name: string
  label?: string
  rows?: number
  className?: string
  labelClassName?: string
}) {
  const { error, getInputProps, setTouched } = useField(name)

  return (
    <div className="col-span-6 flex flex-col gap-1.5">
      {label && <label className={labelClassName ?? `text-ui-sm`}>{label}</label>}
      <textarea
        rows={rows}
        {...getInputProps({
          onChange: () => setTouched(true),
        })}
        className={`w-full rounded-md border border-default px-3 py-2 text-sm shadow-sm placeholder:text-minimal focus:border-(--fg-subtle) focus:outline-none focus:ring-1 focus:ring-(--fg-subtle) ${className ?? ``}`}
      />
      {error && <span className="text-xs text-red-500">{error}</span>}
    </div>
  )
}
